import { api, getHeaders } from '@/api/client';
import { ApiError } from '@/api/errors';
import type { ApiResponse } from '@/api/schema/common';
import { toSearchParams, validateResponse } from '@/api/utils';
import { i18n } from '@/i18n/i18n';

import type { ApprovalsCriteria, FleetJobsCriteria, SetupCalendarRequest, SetupRunningHoursRequest } from './endpoints';
import { readBulkCompletionFailure, type BulkCompletionResultBody } from './readBulkCompletionFailure';
import {
  approvalDashboardItemArrayApiResponseSchema,
  fleetJobInstanceArrayApiResponseSchema,
  type ApprovalDashboardItem,
  type FleetJobInstance
} from './schema';

const BASE_URL = '/pms/api/vesselJobs';

export const getFleetJobInstances = async (criteria?: FleetJobsCriteria): Promise<FleetJobInstance[]> => {
  const query = toSearchParams(criteria);
  const url = query ? `${BASE_URL}/jobs?${query}` : `${BASE_URL}/jobs`;
  const response = await api.get<ApiResponse<FleetJobInstance[]>>(url, getHeaders());
  const parsed = validateResponse(fleetJobInstanceArrayApiResponseSchema, response.data);
  return parsed.Data;
};

export const setupRunningHours = async (jobId: string, request: SetupRunningHoursRequest): Promise<void> => {
  await api.post<ApiResponse<string>>(`${BASE_URL}/${jobId}/setupRunningHours`, request, getHeaders());
};

export const setupCalendar = async (jobId: string, request: SetupCalendarRequest): Promise<void> => {
  await api.post<ApiResponse<string>>(`${BASE_URL}/${jobId}/setupCalendar`, request, getHeaders());
};

export interface CompleteJobRequest {
  VesselJobInstances: {
    VesselJobInstanceId: string;
    CompletionRunningHours: number | null;
  }[];
  CompletionDate: string;
  ClosingComments: string;
  ConditionParameterName?: string;
  ConditionReading?: number;
  ConditionUnit?: string;
}

/**
 * Complete one or more scheduled jobs.
 *
 * **A 2xx is not automatically a success here** (CHR-1078 ADR-0127, deviation D1). On the workflow-kernel
 * path this endpoint decides each item on its own and answers `207` with the refused ones accounted in the
 * body, which axios resolves like any other 2xx. So the body is read and a refusal is re-raised as the
 * `ApiError` the callers' error handling already understands, flagged human-readable because the reason is
 * the server's own operator-facing sentence. Without this a refused completion showed a success toast.
 */
export const completeJob = async (request: CompleteJobRequest): Promise<void> => {
  // Both the 200 and the 207 answer a bare BulkApprovalResult (ADR-0127's envelope carve-out for the bulk
  // lifecycle endpoints), not an OperationResult wrapper - the annotation describes the actual payload.
  const response = await api.post<BulkCompletionResultBody>(`${BASE_URL}/completeJob`, request, getHeaders());

  const failureReason = readBulkCompletionFailure(response.data);
  if (failureReason !== null) {
    throw new ApiError(failureReason === '' ? i18n.t('pms.markAsDone.error') : failureReason, response.status, {
      IsHumanErrorMessage: failureReason !== ''
    });
  }
};

export const getApprovals = async (criteria?: ApprovalsCriteria): Promise<ApprovalDashboardItem[]> => {
  const query = toSearchParams(criteria);
  const url = query ? `${BASE_URL}/approvals?${query}` : `${BASE_URL}/approvals`;
  const response = await api.get<ApiResponse<ApprovalDashboardItem[]>>(url, getHeaders());
  const parsed = validateResponse(approvalDashboardItemArrayApiResponseSchema, response.data);
  return parsed.Data;
};
